import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { X, Send, MailQuestion, Ghost, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  sent: "text-[#0033FF]",
  viewed: "text-black",
  responded: "text-[#34C759]",
  rejected: "text-[#FF3B30]",
  queued: "text-gray-500",
};

const STAGE_META = {
  1: { label: "Nudge", desc: "7-day polite check-in", accent: "border-[#0033FF] text-[#0033FF] hover:bg-[#0033FF]" },
  2: { label: "Firm",  desc: "14-day respectful push",  accent: "border-black text-black hover:bg-black" },
  3: { label: "Exit",  desc: "21-day withdrawal",       accent: "border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30]" },
};

export default function Applications() {
  const { t } = useI18n();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [followupsByApp, setFollowupsByApp] = useState({}); // id -> { [stage]: body }
  const [sendingKey, setSendingKey] = useState(null); // `${appId}-${stage}`
  const [followupView, setFollowupView] = useState(null);
  const [ghostData, setGhostData] = useState([]);
  const [ghostOpen, setGhostOpen] = useState(false);

  const load = async () => {
    try {
      const [a, f, g] = await Promise.allSettled([
        api.get("/applications"),
        api.get("/followups"),
        api.get("/intel/ghost-rate"),
      ]);
      if (a.status === "fulfilled") setApps(a.value.data);
      if (f.status === "fulfilled") {
        const map = {};
        f.value.data.forEach((x) => {
          const stage = x.stage || 1; // legacy followups default to stage 1
          if (!map[x.application_id]) map[x.application_id] = {};
          map[x.application_id][stage] = { body: x.body, sent_at: x.sent_at };
        });
        setFollowupsByApp(map);
      }
      if (g.status === "fulfilled") setGhostData(g.value.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sendStage = async (e, appId, stage) => {
    e.stopPropagation();
    const key = `${appId}-${stage}`;
    setSendingKey(key);
    try {
      const r = await api.post(`/applications/${appId}/followup/stage`, { stage });
      toast.success(`Stage ${stage} follow-up sent`);
      setFollowupView({ app_id: appId, stage, body: r.data.body });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Follow-up failed");
    } finally {
      setSendingKey(null);
    }
  };

  const openView = (e, appId, stage) => {
    e.stopPropagation();
    const fu = followupsByApp[appId]?.[stage];
    if (fu) setFollowupView({ app_id: appId, stage, body: fu.body });
  };

  const topGhost = useMemo(
    () => ghostData.slice().sort((a, b) => b.ghost_rate - a.ghost_rate).slice(0, 6),
    [ghostData]
  );
  const worstGhost = topGhost[0];

  return (
    <div className="space-y-8" data-testid="applications-page">
      <div>
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">/ {t("applications")}</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
          {apps.length} application{apps.length === 1 ? "" : "s"}
        </h1>
        <p className="text-gray-600 mt-2 text-sm">
          Tap any row to view the tailored cover letter. No reply? Send a <span className="font-bold text-black">Nudge</span>,
          then <span className="font-bold text-black">Firm</span>, then <span className="font-bold text-[#FF3B30]">Exit</span> —
          each escalation drafted for you and tracked.
        </p>
      </div>

      {/* Ghost-rate widget */}
      {ghostData.length > 0 && (
        <div className="border border-black/10 bg-[#F8F9FA] p-5" data-testid="ghost-rate-widget">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <Ghost className="w-5 h-5 text-[#FF3B30]" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF3B30]">Ghost radar</div>
                <div className="font-display font-bold text-lg">
                  {worstGhost
                    ? `${worstGhost.company} ghosts ${worstGhost.ghost_rate}% of applicants`
                    : "Reply rates across companies"}
                </div>
              </div>
            </div>
            <button
              onClick={() => setGhostOpen((v) => !v)}
              className="text-sm font-bold border border-black/15 px-4 py-2 hover:bg-black hover:text-white transition-colors"
              data-testid="toggle-ghost"
            >
              {ghostOpen ? "Hide" : "See full list"}
            </button>
          </div>
          {ghostOpen && (
            <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {topGhost.map((g) => (
                <div key={g.company} className="border border-black/10 bg-white p-3 flex items-center justify-between gap-3" data-testid={`ghost-${g.company}`}>
                  <div className="min-w-0">
                    <div className="font-display font-bold truncate">{g.company}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">
                      {g.total_applications} applicants tracked
                    </div>
                  </div>
                  <div className={`font-display font-black text-2xl ${g.ghost_rate >= 70 ? "text-[#FF3B30]" : g.ghost_rate >= 40 ? "text-black" : "text-[#34C759]"}`}>
                    {g.ghost_rate}%
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="font-mono text-sm text-gray-500">loading…</div>
      ) : apps.length === 0 ? (
        <div className="border border-dashed border-black/20 p-16 text-center text-gray-500">
          No applications yet. Head to the search engine and launch your first bulk-apply.
        </div>
      ) : (
        <div className="border border-black/10 overflow-x-auto" data-testid="applications-table">
          <table className="w-full text-start border-collapse min-w-[900px]">
            <thead>
              <tr className="border-b border-black/20 text-xs font-bold uppercase tracking-widest">
                <th className="text-start p-4">Job</th>
                <th className="text-start p-4">Company</th>
                <th className="text-start p-4">Sent</th>
                <th className="text-start p-4">Status</th>
                <th className="text-start p-4">Follow-up sequence</th>
              </tr>
            </thead>
            <tbody>
              {apps.map((a) => {
                const fuMap = followupsByApp[a.id] || {};
                return (
                  <tr
                    key={a.id}
                    onClick={() => setActive(a)}
                    className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                    data-testid={`app-row-${a.id}`}
                  >
                    <td className="p-4 font-display font-bold">{a.job_title}</td>
                    <td className="p-4 text-gray-700">
                      <div>{a.company}</div>
                      <div className="font-mono text-[10px] text-gray-500 uppercase tracking-widest">
                        {a.city}
                      </div>
                    </td>
                    <td className="p-4 font-mono text-xs text-gray-500">
                      {new Date(a.sent_at).toLocaleDateString()}
                    </td>
                    <td className={`p-4 font-mono text-xs uppercase tracking-widest font-bold ${STATUS_COLORS[a.status] || ""}`}>
                      {a.status}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {[1, 2, 3].map((stage) => {
                          const m = STAGE_META[stage];
                          const done = !!fuMap[stage];
                          const key = `${a.id}-${stage}`;
                          if (done) {
                            return (
                              <button
                                key={stage}
                                onClick={(e) => openView(e, a.id, stage)}
                                className="inline-flex items-center gap-1 px-2 py-1.5 text-xs border border-[#34C759] text-[#34C759] hover:bg-[#34C759] hover:text-white transition-colors"
                                title={`Stage ${stage} sent — view`}
                                data-testid={`view-stage-${stage}-${a.id}`}
                              >
                                <MailQuestion className="w-3 h-3" /> S{stage}
                              </button>
                            );
                          }
                          return (
                            <button
                              key={stage}
                              onClick={(e) => sendStage(e, a.id, stage)}
                              disabled={sendingKey === key}
                              title={m.desc}
                              className={`inline-flex items-center gap-1 px-2 py-1.5 text-xs border ${m.accent} hover:text-white transition-colors disabled:opacity-50`}
                              data-testid={`send-stage-${stage}-${a.id}`}
                            >
                              <Send className="w-3 h-3" />
                              {sendingKey === key ? "…" : `S${stage} ${m.label}`}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {active && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="app-detail-modal">
          <div className="bg-white w-full max-w-2xl border border-black/10 max-h-[90vh] flex flex-col">
            <div className="flex items-start justify-between p-6 border-b border-black/10">
              <div>
                <div className="font-mono text-xs uppercase tracking-widest text-gray-500 mb-1">
                  Cover letter — auto-generated
                </div>
                <h3 className="font-display font-bold text-2xl">{active.job_title}</h3>
                <div className="text-gray-600 text-sm">{active.company} • {active.city}</div>
              </div>
              <button onClick={() => setActive(null)} className="p-1 hover:bg-gray-100" data-testid="close-app-modal">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto whitespace-pre-wrap text-gray-800 leading-relaxed">
              {active.cover_letter}
            </div>
          </div>
        </div>
      )}

      {followupView && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="followup-modal">
          <div className="bg-white w-full max-w-2xl border border-black/10 max-h-[90vh] flex flex-col">
            <div className="flex items-start justify-between p-6 border-b border-black/10">
              <div>
                <div className="font-mono text-xs uppercase tracking-widest text-[#FF3B30] mb-1">
                  Follow-up · stage {followupView.stage || 1}
                </div>
                <h3 className="font-display font-bold text-2xl">
                  {STAGE_META[followupView.stage]?.label || "Follow-up"} email
                </h3>
              </div>
              <button onClick={() => setFollowupView(null)} className="p-1 hover:bg-gray-100">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto whitespace-pre-wrap text-gray-800 leading-relaxed">
              {followupView.body}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
